import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { Button, EmptyState, Skeleton } from '@hrms/ui';
import { isPlatformHost } from '../lib/platformHost.js';
import { currentOrgSlug } from '../lib/subdomain.js';
import { useSession } from './SessionProvider.js';
import { hasSignedInBefore, markSignedIn } from './signedInBefore.js';
import { hasSeenIntro } from '../features/intro/introSeen.js';

/**
 * Where a signed-out visitor is sent. The bare landing (`/` on the app domain, or the phone app's
 * first screen) goes to sign-up for someone who has never signed in on this device: a first-time
 * visitor, and /signup carries an "Already registered? Sign in" link. In the phone app that
 * first-timer sees the intro slides first, once per install: they end on sign-up or sign-in, and
 * leaving them by any button marks them seen, so the next launch goes straight to /signup (which
 * resumes its own draft). The website keeps going straight to /signup. Someone who has signed in here
 * before goes to /login, so an employee whose session lapsed is not shown sign-up or the intro.
 * Everything else goes to /login with `from`: a deep link (a shared course, an emailed approval) is
 * followed by someone who already has an account, and a company's own subdomain (`acme.<domain>`) is
 * for that company's people, not for creating another one. Signing out from the header also goes to
 * /login (AppShell).
 *
 * Pure, so the platform and storage reads stay in RequireAuth; the defaults are the website's
 * answer, which is what every caller got before the intro existed.
 */
export function signedOutDestination(
  pathname: string,
  orgSlug: string | null,
  signedInBefore: boolean,
  onPlatformHost = false,
  isNative = false,
  introSeen = true,
): '/intro' | '/signup' | '/login' {
  // The management portal has no sign-up page (it would bounce back to `/` and loop forever).
  if (onPlatformHost) return '/login';
  if (signedInBefore || pathname !== '/' || orgSlug !== null) return '/login';
  return isNative && !introSeen ? '/intro' : '/signup';
}

/**
 * The one authentication gate, as a pathless layout route around everything except the public auth
 * screens (/login, /signup, ...).
 *
 * One guard at the routing root rather than one per screen: per-route guards drift — the fourth
 * screen someone adds forgets it, and that screen is public until a reviewer notices. Here a
 * route is protected by being nested under this element, which is the default place to put it.
 *
 * Anonymous carries the current location as `from` so signing in returns the person to the page
 * they asked for, not to Home.
 */
export function RequireAuth() {
  const session = useSession();
  const location = useLocation();

  useEffect(() => {
    if (session.status === 'authenticated') markSignedIn();
  }, [session.status]);

  switch (session.status) {
    case 'loading':
      return (
        <div className="mx-auto max-w-6xl space-y-4 px-6 py-8" aria-busy="true">
          <Skeleton className="h-10 w-1/3" />
          <Skeleton className="h-40" />
          <Skeleton className="h-64" />
        </div>
      );
    case 'anonymous':
      return (
        <Navigate
          to={signedOutDestination(
            location.pathname,
            currentOrgSlug(),
            hasSignedInBefore(),
            isPlatformHost(),
            Capacitor.isNativePlatform(),
            hasSeenIntro(),
          )}
          state={{ from: location.pathname + location.search }}
          replace
        />
      );
    case 'error':
      return (
        <EmptyState
          title="Can't reach the server"
          description="Your sign-in is still valid. Check your connection and try again."
          action={
            <Button variant="primary" onClick={session.retry}>
              Retry
            </Button>
          }
        />
      );
    case 'authenticated':
      return <Outlet />;
  }
}
